import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchPatientByPhone,
  createOrder,
  updateOrder,
  fetchOrderById,
  fetchStates,
  fetchPharmacies,
  fetchMedicines,
  fetchPharmaciesAndMedicines,
  addPatientAddress,
  fetchProvidersByState,
  fetchProviders,
  fetchProviderById,
  syncPharmacyAddresses,
  api
} from "../services/api";
import PatientInfoSection from "../components/order/PatientInfoSection";
import ShippingAddressSection from "../components/order/ShippingAddressSection";
import OrderRequestDetails from "../components/order/OrderRequestDetails";
import OrderSummary from "../components/order/OrderSummary";
import OrderStatusPopup from "../components/order/OrderStatusPopup";

// Styled components
const PageWrapper = styled.div`
  padding: 24px;
  background-color: var(--bg-primary, #1a1a1a);
  min-height: 100vh;
  width: 100%;
`;

const PageHeaderContainer = styled.div`
  margin-bottom: 24px;
  max-width: 1200px;
  // margin: 0 auto 24px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 12px;
`;

const PageDescription = styled.p`
  color: var(--text-secondary, #aaa);
  font-size: 1rem;
  max-width: 800px;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const FormTitle = styled.h2`
  font-size: 1.75rem;
  color: var(--primary, #6366f1);
  margin-bottom: 32px;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color, #3a3a3a);
  padding-bottom: 16px;
`;

const SuccessMessage = styled.div`
  padding: 16px;
  background-color: rgba(16, 185, 129, 0.1);
  color: var(--success, #10b981);
  border-radius: 8px;
  margin-bottom: 24px;
  font-weight: 500;
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto 24px;

  svg {
    margin-right: 12px;
  }
`;

// Component definition
function CreateOrderPage({ editMode = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  // const [id, setId] = useState();

  // Helper function to calculate BMI
  const calculateBMI = (height, weight) => {
    if (!height || !weight) return "";

    const heightInMeters = parseFloat(height) * 0.0254;
    const weightInKg = parseFloat(weight) * 0.453592;
    const bmi = weightInKg / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  // Form state
  const [formData, setFormData] = useState({
    patientId: "",
    patientPhone: "",
    patientName: "",
    patientEmail: "",
    patientAddress: {
      street: "",
      city: "",
      state: "",
      zipcode: ""
    },
    selectedAddressId: "",
    newAddress: {
      street: "",
      address2: "",
      city: "",
      state: "",
      zipcode: ""
    },
    stateId: "",
    pharmacyId: "",
    pharmacy: null, // Added to store pharmacy object for edit mode
    medicines: [
      {
        id: "",
        name: "",
        quantity: 1,
        variant_id: "",
        price: 0,
        description: "",
        in_stock: true,
        medicine_sku: "",
        medicine_id: "",
        dosage_instructions: [],
        all_dosage_instructions: [],
        variants: [],
        useCustomDosage: false,
        customDosageInstructions: [],
        useCustomDosage: false,
        customDosageInstructions: []
      }
    ],
            customDosageInstructions: [], // Removed - no longer needed as global array
    shippingFee: 30,
    discount: 0,
    filledBy: "",
    notes: "",
    clinical_notes: "",
    addressSaved: false,
    orderType: "new",
    isNewAddress: false,
    pharmacy_addresses: [], // Addresses synced from the selected pharmacy's API
    selectedPharmacyAddressId: "", // Selected pharmacy address ID
    pharmacyAddress: {
      // Selected pharmacy address details
      id: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: ""
    },
    providerId: ""
  });

  // Loading states
  const [loading, setLoading] = useState({
    searchingPatient: false,
    loadingStates: false,
    loadingPharmacies: false,
    loadingMedicines: false,
    submittingForm: false,
    savingAddress: false
  });

  // Data states
  const [states, setStates] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [patientFound, setPatientFound] = useState(false);
  const [patientEditUnsaved, setPatientEditUnsaved] = useState(false);
  const [patientAddresses, setPatientAddresses] = useState([]);
  const [pharmaciesAndMedicines, setPharmaciesAndMedicines] = useState([]);
  const [providersForState, setProvidersForState] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providersStateAbbr, setProvidersStateAbbr] = useState("");
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [apiResults, setApiResults] = useState(null);
  const [originalOrderData, setOriginalOrderData] = useState(null); // Added to store original order data

  // Pharmacy address sync state: addresses in the Shipping Address section
  // come from the pharmacy selected for the order
  const [pharmacyAddressSync, setPharmacyAddressSync] = useState({
    loading: false,
    supported: true,
    error: ""
  });
  const pharmacyAddressSyncSeq = useRef(0);

  // Sync the patient's addresses from the selected pharmacy's API whenever
  // the pharmacy (or patient) changes
  useEffect(() => {
    const seq = ++pharmacyAddressSyncSeq.current;

    if (!formData.pharmacyId || !formData.patientId) {
      setPharmacyAddressSync({ loading: false, supported: true, error: "" });
      setFormData((prev) => ({
        ...prev,
        pharmacy_addresses: [],
        selectedPharmacyAddressId: "",
        pharmacyAddress: {
          id: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          zipcode: ""
        }
      }));
      return;
    }

    setPharmacyAddressSync({ loading: true, supported: true, error: "" });
    setFormData((prev) => ({
      ...prev,
      pharmacy_addresses: [],
      selectedPharmacyAddressId: ""
    }));

    syncPharmacyAddresses(formData.patientId, formData.pharmacyId)
      .then((data) => {
        if (seq !== pharmacyAddressSyncSeq.current) return; // Stale response
        setPharmacyAddressSync({
          loading: false,
          supported: data.supported !== false,
          error: ""
        });
        setFormData((prev) => ({
          ...prev,
          pharmacy_addresses: data.addresses || []
        }));
      })
      .catch((error) => {
        if (seq !== pharmacyAddressSyncSeq.current) return; // Stale response
        setPharmacyAddressSync({
          loading: false,
          supported: true,
          error: error.message || "Failed to sync pharmacy addresses"
        });
      });
  }, [formData.pharmacyId, formData.patientId]);

  // When a shipping address in another state is picked or imported, the
  // pharmacy, provider and medicines are kept if they are still valid there.
  // These refs mark the state whose freshly loaded lists must be checked.
  const pendingStateReconcile = useRef(null);
  const providerRecheckState = useRef(null);
  const [stateChangeNotices, setStateChangeNotices] = useState([]);
  const formDataRef = useRef(null);
  formDataRef.current = formData;

  // The notices are stale once the user picks a provider or pharmacy again
  useEffect(() => {
    if (formData.providerId) setStateChangeNotices([]);
  }, [formData.providerId]);
  useEffect(() => {
    if (formData.pharmacyId) setStateChangeNotices([]);
  }, [formData.pharmacyId]);

  const emptyMedicineRow = () => ({
    id: "",
    medicine_id: "",
    medicine_name: "",
    quantity: 1,
    variant_id: "",
    price: 0,
    description: "",
    in_stock: true,
    medicine_sku: "",
    dosage_instructions: [],
    all_dosage_instructions: [],
    variants: [],
    compounding_context: "",
    useCustomDosage: false,
    customDosageInstructions: []
  });

  // Called once the new state's pharmacies and medicines are loaded: keep the
  // selections that are still offered there, drop only the ones that are not.
  const reconcileSelectionsForState = (stateId, pharmacyList, medicineList) => {
    if (pendingStateReconcile.current !== stateId.toString()) return;
    pendingStateReconcile.current = null;

    const current = formDataRef.current;
    if (!current || !current.pharmacyId) return;

    const stateName =
      states.find((s) => s.state_id?.toString() === stateId.toString())
        ?.state_name || "the new state";
    const pharmacyId = current.pharmacyId.toString();
    const pharmacyKept = pharmacyList.some(
      (p) => p.id?.toString() === pharmacyId
    );

    if (!pharmacyKept) {
      const pharmacyName =
        pharmacies.find((p) => p.id?.toString() === pharmacyId)?.name ||
        "The selected pharmacy";
      providerRecheckState.current = null;
      // Replaces any provider notice: the provider goes with the pharmacy
      setStateChangeNotices([
        `${pharmacyName} does not ship to ${stateName}, so the pharmacy, provider and medicines were cleared.`
      ]);
      setFormData((prev) => ({
        ...prev,
        pharmacyId: "",
        providerId: "",
        medicines: [emptyMedicineRow()]
      }));
      return;
    }

    const isOffered = (row) =>
      !row.medicine_id ||
      medicineList.some(
        (m) =>
          m.pharmacy_id?.toString() === pharmacyId &&
          m.medicine_id?.toString() === row.medicine_id.toString()
      );
    const dropped = current.medicines.filter((row) => !isOffered(row));
    if (dropped.length === 0) return;

    setStateChangeNotices((prev) => [
      ...prev,
      `Not available in ${stateName} and removed: ${dropped
        .map((row) => row.medicine_name || "a medicine")
        .join(", ")}.`
    ]);
    setFormData((prev) => {
      const kept = prev.medicines.filter(isOffered);
      return {
        ...prev,
        medicines: kept.length > 0 ? kept : [emptyMedicineRow()]
      };
    });
  };

  // Select an existing patient address as the shipping address. A change of
  // state keeps whatever is still valid in the new state (see above), so the
  // shipping address can be picked/changed after the pharmacy and medicines.
  const applyShippingAddressSelection = (selectedAddress) => {
    if (!selectedAddress) return;

    const matchedState = states.find(
      (state) =>
        state.state_name.toLowerCase() ===
          selectedAddress.state?.toLowerCase() ||
        state.state_abbreviation.toLowerCase() ===
          selectedAddress.state?.toLowerCase()
    );
    const newStateId = matchedState?.state_id?.toString() || "";
    // A recognised state is re-checked against that state's lists once they
    // load; an unrecognised one cannot be checked, so it still clears.
    const keepSelections = Boolean(matchedState);

    if (newStateId !== (formData.stateId || "")) {
      setStateChangeNotices([]);
      pendingStateReconcile.current = keepSelections ? newStateId : null;
      providerRecheckState.current = keepSelections ? newStateId : null;
    }

    setFormData((prev) => {
      const stateChanged =
        newStateId !== (prev.stateId || "") && !keepSelections;
      return {
        ...prev,
        selectedAddressId: selectedAddress.address_id.toString(),
        shipping_address_id: selectedAddress.address_id.toString(),
        // Picking or importing an existing address supersedes any "+ Add New
        // Address" the user saved earlier; leaving this true makes submit read
        // newAddress.address_id, which handleImportAddress has already nulled.
        isNewAddress: false,
        patientAddress: {
          id: selectedAddress.address_id,
          street: selectedAddress.address1,
          apt: selectedAddress.address2 || "",
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipcode: selectedAddress.zipcode,
          imported: selectedAddress.imported
        },
        shipping_address: selectedAddress,
        stateId: newStateId,
        // An unrecognised state cannot be validated, so it clears the selections
        ...(stateChanged
          ? {
              pharmacyId: "",
              providerId: "",
              medicines: [emptyMedicineRow()]
            }
          : {})
      };
    });

    // The stateId effect below loads the new state's lists; it skips edit mode
    // once the order is loaded, so load them here in that case.
    const stateChanged = newStateId !== (formData.stateId || "");
    if (stateChanged && matchedState && editMode && originalOrderData) {
      loadPharmaciesAndMedicines(matchedState.state_id.toString());
    }
  };

  // Load user data from localStorage on component mount
  useEffect(() => {
    loadStates();

    // Get user info from localStorage
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const userObject = JSON.parse(userString);
        if (userObject && userObject.username) {
          setFormData((prev) => ({
            ...prev,
            filledBy: userObject.username
          }));
        }
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  }, []);

  // Load states, pharmacies, and medicines on component mount
  useEffect(() => {
    // If in edit mode, load the order data after states are loaded
    if (editMode && id && states.length > 0) {
      loadOrderData();
    }
  }, [editMode, id, states.length]);

  // Helper: load providers for a state abbreviation, optionally forcing refresh
  const loadProvidersForState = async (stateAbbr, { force = false } = {}) => {
    if (!stateAbbr) {
      setProvidersForState([]);
      setProvidersStateAbbr("");
      setFormData((prev) => ({ ...prev, providerId: "" }));
      return;
    }

    const normalized = stateAbbr.toString().trim().toUpperCase();
    if (!force && normalized === providersStateAbbr) return;

    setFormData((prev) => ({ ...prev, providerId: "" }));
    setProvidersForState([]);
    setProvidersStateAbbr(normalized);

    try {
      setLoadingProviders(true);
      const data = await fetchProvidersByState(normalized);
      setProvidersForState(data?.providers || []);
    } catch (err) {
      console.error("Failed to load providers by state:", err);
      setProvidersForState([]);
      setErrors((prev) => ({
        ...prev,
        providerId:
          err?.message || "Failed to load providers for selected state"
      }));
    } finally {
      setLoadingProviders(false);
    }
  };

  useEffect(() => {
    if (!formData.pharmacyId || !formData.stateId) {
      setProvidersForState([]);
      setLoadingProviders(false);
      return;
    }
    let cancelled = false;
    const existingProviderId = formData.providerId;
    // After a shipping-address state change the provider stays only if licensed there
    const recheckProvider = providerRecheckState.current === formData.stateId;
    if (recheckProvider) providerRecheckState.current = null;
    setLoadingProviders(true);
    fetchProviders({ pharmacy_id: formData.pharmacyId, state_id: formData.stateId })
      .then(async (data) => {
        if (cancelled) return;
        const list = data.providers || [];
        const providerListed = list.find(p => p.provider_id.toString() === existingProviderId);
        if (recheckProvider && existingProviderId && !providerListed) {
          setFormData((prev) =>
            prev.providerId === existingProviderId ? { ...prev, providerId: "" } : prev
          );
          setStateChangeNotices((prev) => [
            ...prev,
            "The selected provider is not licensed in the new state for this pharmacy - please choose another."
          ]);
        } else if (existingProviderId && !providerListed) {
          const extra = await fetchProviderById(parseInt(existingProviderId)).catch(() => null);
          if (cancelled) return;
          if (extra) list.unshift(extra);
        }
        list.sort((a, b) => {
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setProvidersForState(list);
      })
      .catch(() => { if (!cancelled) setProvidersForState([]); })
      .finally(() => { if (!cancelled) setLoadingProviders(false); });
    return () => { cancelled = true; };
  }, [formData.pharmacyId, formData.stateId]);

  // When state changes, load pharmacies and medicines for that state
  // Modified to not trigger when in edit mode and first loading
  useEffect(() => {
    if (formData.stateId && (!editMode || !originalOrderData)) {
      console.log(
        "formData.stateId changed, loading pharmacies and medicines:",
        formData.stateId
      );
      loadPharmaciesAndMedicines(formData.stateId);
    } else if (!formData.stateId && !originalOrderData) {
      // Reset pharmacy and medicine selections when state is cleared
      // But don't reset if we're loading an order
      setPharmacies([]);
      setFormData((prev) => ({
        ...prev,
        pharmacyId: ""
      }));
      setMedicines([]);
    }
  }, [formData.stateId]);

  // Load states from API
  const loadStates = async () => {
    setLoading((prev) => ({ ...prev, loadingStates: true }));
    try {
      const statesData = await fetchStates();
      console.log("States loaded in component:", statesData);

      // Ensure each state has the required properties
      const processedStates = statesData.map((state, index) => {
        return {
          ...state,
          id: state.id || `state-${index}`,
          state_name: state.state_name || state.name || `State ${index}`,
          state_code: state.state_code || state.code || `S${index}`,
          state_abbreviation:
            state.state_abbreviation ||
            state.abbreviation ||
            state.state_code ||
            state.code ||
            `S${index}`
        };
      });

      setStates(processedStates);
    } catch (error) {
      console.error("Error loading states:", error);
    } finally {
      setLoading((prev) => ({ ...prev, loadingStates: false }));
    }
  };

  // Load order data for edit mode - completely rewritten
  const loadOrderData = async () => {
    try {
      setLoading((prev) => ({ ...prev, submittingForm: true }));
      const orderData = await fetchOrderById(id);
      console.log(
        "Order data received:Order data received:Order data received:Order data received:",
        orderData
      );
      //  selectHighestIdAddress(addresses);
      // Store the original order data for reference
      setOriginalOrderData(orderData);

      if (orderData) {
        // Set patient found state since we have an order with patient info
        setPatientFound(true);

        // Resolve state from shipping address first (patient's actual state),
        // fall back to pharmacy.states only if shipping address has no state.
        let stateId = "";

        if (orderData.shipping_address?.state) {
          const shippingState = orderData.shipping_address.state;
          const matchingState = states.find(
            (state) =>
              state.state_abbreviation === shippingState ||
              state.state_name === shippingState
          );
          if (matchingState) {
            stateId =
              matchingState.state_id?.toString() ||
              matchingState.id?.toString();
          }
        }

        if (!stateId) {
          const pharmacyStates = orderData.pharmacy?.states || [];
          for (const stateIdFromPharmacy of pharmacyStates) {
            const matchingState = states.find(
              (state) =>
                state.id == stateIdFromPharmacy ||
                state.state_id == stateIdFromPharmacy
            );
            if (matchingState) {
              stateId =
                matchingState.state_id?.toString() ||
                matchingState.id?.toString();
              break;
            }
          }
        }

        // Load pharmacies and medicines for the state first
        if (stateId) {
          console.log("Loading pharmacies for state ID:", stateId);
          await loadPharmaciesAndMedicines(stateId, true); // true indicates we're in edit mode
        }

        console.log("orderData.order_items", orderData);

        // Extract custom dosage instructions if they exist in the order data - now bound to medicines
        // const customDosages = orderData.custom_dosage_instructions || orderData.custom_dosage || [];

        setFormData((prev) => {
          // Prepare medicines array
          const medicinesArray = orderData.order_items?.map((item) => ({
            id: item.order_item_id?.toString() || "",
            medicine_id: item.medicine?.medicine_id?.toString() || "",
            medicine_name: item.medicine?.medicine_name || "",
            quantity: 1,
            variant_id: item.variant?.variant_id?.toString() || "",
            variant: item.variant || null,
            price: parseFloat(item.price),
            description: item.medicine?.description || "",
            in_stock: true,
            medicine_sku: item.medicine?.medicine_sku || "",
            dosage_instructions: item.medicine?.dosage_instructions || [],
            dosage_instruction_id:
              item.dosage_instructions?.[0]?.dosage_instruction_id?.toString() ||
              "",
            compounding_context: item.compounding_context || "",
            // Check if this medicine has custom dosage instructions
            useCustomDosage: item.dosage_instructions?.some(di => di.is_custom) || false,
            customDosageInstructions: item.dosage_instructions
              ?.filter(di => di.is_custom)
              ?.map(di => di.instruction) || [],
            dosage_instructions: item.dosage_instructions.map((item) => {
              const { dosage_id, ...rest } = item;
              return {
                dosage_instruction_id: dosage_id,
                ...rest
              };
            }),
            variants: item.medicine?.variants || []
          })) || [
            {
              id: "",
              medicine_id: "",
              medicine_name: "",
              quantity: 1,
              variant_id: "",
              price: 0,
              description: "",
              in_stock: true,
              medicine_sku: "",
              dosage_instructions: [],
              all_dosage_instructions: [],
              variants: [],
              useCustomDosage: false,
              customDosageInstructions: []
            }
          ];

          return {
            ...prev,
            patientId: orderData.patient_id,
            patientName: orderData.patient_name,
            patientPhone: orderData.patient_phone,
            patientEmail: orderData.patient_email,
            patientGender: orderData.patient_gender || "",
            selectedAddressId:
              orderData.shipping_address?.address_id?.toString() || "",
            stateId: stateId,
            pharmacyId: orderData.pharmacy?.pharmacy_id?.toString() || "",
            pharmacy: orderData.pharmacy, // Store the complete pharmacy object
            patientAddress: {
              id: orderData.shipping_address?.address_id || "",

              street: orderData.shipping_address?.address1 || "",
              city: orderData.shipping_address?.city || "",
              state: orderData.shipping_address?.state || "",
              zipcode: orderData.shipping_address?.zipcode || ""
            },
            newAddress: {
              street: orderData.shipping_address?.address1 || "",
              address2: orderData.shipping_address?.address2 || "",
              city: orderData.shipping_address?.city || "",
              state: orderData.shipping_address?.state || "",
              zipcode: orderData.shipping_address?.zipcode || ""
            },
            customDosageInstructions: [], // No longer needed as global array
            shippingFee: parseFloat(orderData.shipping_fee) || 0,
            discount: parseFloat(orderData.discount) || 0,
            filledBy: orderData.filled_by?.toString() || "",
            notes: orderData.notes || "",
            clinical_notes: orderData.clinical_notes || "",
            addressSaved: true,
            orderType: orderData.order_type || "new",
            isNewAddress: false,
            medicines: medicinesArray,
            providerId:
              orderData.provider_info?.provider_id?.toString?.() ||
              orderData.provider_id?.toString?.() ||
              ""
          };
        });

        // Check if the pharmacy from the order exists in our loaded pharmacies
        const pharmacyExists = pharmacies.some(
          (p) =>
            p.id === orderData.pharmacy?.pharmacy_id ||
            p.pharmacy_id === orderData.pharmacy?.pharmacy_id
        );

        if (!pharmacyExists && orderData.pharmacy) {
          // If the pharmacy from order doesn't exist in our loaded pharmacies, add it
          console.log(
            "Adding pharmacy from order to pharmacies list:",
            orderData.pharmacy
          );
          setPharmacies((prev) => [
            ...prev,
            {
              id: orderData.pharmacy.pharmacy_id,
              pharmacy_id: orderData.pharmacy.pharmacy_id,
              name: orderData.pharmacy.pharmacy_name,
              address: orderData.pharmacy.pharmacy_address,
              phone: "",
              email: ""
            }
          ]);
        }
      } else {
        setErrors((prev) => ({ ...prev, submit: "Order not found" }));
      }
    } catch (error) {
      console.error("Error loading order data:", error);
      setErrors((prev) => ({ ...prev, submit: "Error loading order data" }));
    } finally {
      setLoading((prev) => ({ ...prev, submittingForm: false }));
    }
  };

  // Add this utility function at the top of your CreateOrderPage component
  const extractDigits = (input) => {
    console.log("input teste", input);
    if (!input) return "";

    // Check if the input starts with a plus sign
    const hasPlus = input.startsWith("+");

    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Return with the plus sign if the original input had one
    return hasPlus ? "+" + digits : digits;
  };

  // Update the handlePatientSearch function
  const handlePatientSearch = async (e) => {
    e.preventDefault();

    if (!formData.patientPhone) {
      setErrors((prev) => ({
        ...prev,
        patientPhone: "Please enter a phone number"
      }));
      return Promise.reject("No phone number provided");
    }

    // Extract only digits from the phone number
    const phoneDigits = extractDigits(formData.patientPhone);

    if (!phoneDigits) {
      setErrors((prev) => ({
        ...prev,
        patientPhone: "Please enter a phone number with at least one digit"
      }));
      return Promise.reject("Invalid phone number");
    }

    setLoading((prev) => ({ ...prev, searchingPatient: true }));
    setErrors((prev) => ({ ...prev, patientPhone: "", submit: "" }));

    try {
      console.log("Searching for patient with phone:", formData.patientPhone);
      console.log("Using digits only:", phoneDigits);

      // Pass the digits-only version to the API function
      const patientData = await fetchPatientByPhone(phoneDigits);
      console.log("API Response:", patientData);

      if (patientData && patientData.length > 0) {
        const patient = patientData[0];
        console.log("Patient found:", patient);

        setFormData((prev) => ({
          ...prev,
          patientId: patient.patient_id,
          patientFirstName: patient.first_name || "",
          patientLastName: patient.last_name || "",
          patientName: `${patient.first_name} ${patient.last_name}`,
          patientEmail: patient.email || "",
          patientPhone: prev.patientPhone, // Keep original format
          patientDOB: patient.date_of_birth || "",
          patientGender: patient.gender || "",
          patientHeight: patient.height || "",
          patientWeight: patient.weight || "",
          patientBMI: calculateBMI(patient.height, patient.weight) || "",
          selectedAddressId: "", // Always reset to empty so user must select
          providerId: "",
          patientAddress: {
            street: "",
            city: "",
            state: "",
            zipcode: ""
          },
          newAddress: {
            street: "",
            apt: "",
            city: "",
            state: "",
            zipcode: ""
          },
          // Pharmacy addresses are synced once a pharmacy is selected
          pharmacy_addresses: [],
          selectedPharmacyAddressId: ""
        }));

        // Reset providers list when a new patient is searched
        loadProvidersForState("");

        setPatientFound(true);

        if (patient.addresses && patient.addresses.length > 0) {
          console.log("Patient addresses:", patient.addresses);

          const sortedAddresses = [...patient.addresses].sort((a, b) => {
            const aId = a && a.address_id ? parseInt(a.address_id) : 0;
            const bId = b && b.address_id ? parseInt(b.address_id) : 0;
            return bId - aId; // Sort by highest ID first
          });
          console.log("sortedAddresses", sortedAddresses);
          setPatientAddresses(sortedAddresses);

          // DO NOT auto-select any address - this is intentionally removed
          // Leave the stateId empty as well until user selects an address
          setFormData((prev) => ({
            ...prev,
            stateId: "" // Clear state ID until user selects an address
          }));
        } else {
          setPatientAddresses([]);
        }

        return Promise.resolve(patientData);
      } else {
        console.log("No patient found with the provided phone number");
        setErrors((prev) => ({
          ...prev,
          patientPhone: "No patient found with this phone number"
        }));
        setPatientFound(false);
        setPatientAddresses([]);
        return Promise.reject("No patient found");
      }
    } catch (error) {
      console.error("Error searching for patient:", error);
      setErrors((prev) => ({
        ...prev,
        patientPhone: "Error searching for patient"
      }));
      setPatientFound(false);
      setPatientAddresses([]);
      return Promise.reject(error);
    } finally {
      setLoading((prev) => ({ ...prev, searchingPatient: false }));
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "stateId") {
      console.log("State selected:", value);
      console.log("Available states:", states);
      const selectedState = states.find(
        (state) =>
          state.id == value ||
          state.id === parseInt(value) ||
          state.id === value.toString()
      );
      console.log("Found state:", selectedState);

      if (selectedState) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      } else {
        console.error(
          "State not found in available states list. Value:",
          value
        );
      }
    } else if (name === "patientPhone") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  // Handle pharmacy selection changes
  const handlePharmacyChange = (e) => {
    const value = e.target.value;

    if (!value) {
      setFormData((prev) => ({
        ...prev,
        pharmacyId: "",
        providerId: "",
        medicines: [
          {
            id: "",
            name: "",
            quantity: 1,
            price: 0,
            description: "",
            in_stock: true,
            medicine_sku: "",
            medicine_id: "",
            dosage_instructions: [],
            all_dosage_instructions: []
          }
        ]
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      pharmacyId: value,
      providerId: "",
      medicines: [
        {
          id: "",
          name: "",
          quantity: 1,
          price: 0,
          description: "",
          in_stock: true,
          medicine_sku: "",
          medicine_id: "",
          dosage_instructions: [],
          all_dosage_instructions: [],
          useCustomDosage: false,
          customDosageInstructions: []
        }
      ]
    }));

    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.includes("medicines")) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  // Handle medicine selection changes
  const handleMedicationChange = (index, field, value) => {
    const updatedMedicines = [...formData.medicines];

    if (field === "medicine_id") {
      const selectedMedicine = medicines.find(
        (med) => med.medicine_id.toString() === value.toString()
      );

      if (selectedMedicine) {
        updatedMedicines[index] = {
          ...updatedMedicines[index],
          medicine_id: value,
          medicine_name: selectedMedicine.medicine_name,
          variants: selectedMedicine.variants || [],
          dosage_instructions: selectedMedicine.dosage_instructions || [],
          variant_id: "",
          dosage_instruction_id: "",
          dosage_instructions_array: [],
          price: 0, // Default price when medicine is selected
          useCustomDosage: false,
          customDosageInstructions: []
        };
      }
    } else if (field === "variant_id") {
      const selectedVariant = updatedMedicines[index].variants.find(
        (variant) => variant.variant_id.toString() === value.toString()
      );
      if (selectedVariant) {
        updatedMedicines[index] = {
          ...updatedMedicines[index],
          variant_id: value,
          variant: selectedVariant
        };
      }
    } else if (field === "dosage_instruction_id") {
      const selectedInstruction = updatedMedicines[
        index
      ].dosage_instructions.find(
        (instruction) =>
          instruction.dosage_instruction_id.toString() === value.toString()
      );
      if (selectedInstruction) {
        updatedMedicines[index] = {
          ...updatedMedicines[index],
          dosage_instruction_id: value,
          dosage_instructions_array: [selectedInstruction]
        };
      }
    } else {
      updatedMedicines[index] = {
        ...updatedMedicines[index],
        [field]: value
      };
    }

    setFormData((prev) => ({
      ...prev,
      medicines: updatedMedicines
    }));

    if (errors[`medicines[${index}].${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`medicines[${index}].${field}`];
        return newErrors;
      });
    }
  };

  // Add more medicine
  const addMedicine = () => {
    if (!formData.pharmacyId) {
      setErrors((prev) => ({
        ...prev,
        medicines: "Please select a pharmacy first."
      }));
      return;
    }

    const newMedicine = {
      medicine_id: "",
      medicine_name: "",
      variants: [],
      variant_id: "",
      variant: null,
      dosage_instructions: [],
      dosage_instruction_id: "",
      dosage_instructions_array: [],
      price: 0,
      compounding_context: "",
      useCustomDosage: false,
      customDosageInstructions: []
    };

    setFormData((prev) => ({
      ...prev,
      medicines: [...prev.medicines, newMedicine]
    }));

    if (errors.medicines) {
      setErrors((prev) => {
        const { medicines, ...rest } = prev;
        return rest;
      });
    }
  };

  // Remove medicine
  const removeMedicine = (index) => {
    if (formData.medicines.length <= 1) {
      setErrors((prev) => ({
        ...prev,
        medicines: "At least one medicine is required"
      }));
      return;
    }

    const updatedMedicines = formData.medicines.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      medicines: updatedMedicines
    }));

    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.includes(`medicines[${index}]`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  // In the PatientInfoSection component, update the handleAddressChange function:

  const handleAddressChange = async (e) => {
    const { value } = e.target;

    // Clear pharmacy-related errors when address changes
    if (errors.pharmacyId || errors.stateId) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.pharmacyId;
        delete updatedErrors.stateId;
        return updatedErrors;
      });
    }

    if (value === "new") {
      setFormData((prev) => ({
        ...prev,
        addressSaved: false,
        selectedAddressId: "new",
        shipping_address_id: null,
        shipping_address: {
          street: "",
          apt: "",
          city: "",
          state: "",
          zipcode: ""
        }
      }));
    } else {
      const selectedAddress = patientAddresses.find(
        (addr) => addr.address_id === parseInt(value)
      );

      if (selectedAddress) {
        applyShippingAddressSelection(selectedAddress);
      }
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};

    if (!formData.patientId) {
      newErrors.patientPhone = "Valid patient is required";
    }
    console.log("formData", formData);
    if (!formData.stateId) {
      newErrors.stateId = "State is required";
    }

    if (!formData.pharmacyId) {
      newErrors.pharmacyId = "Pharmacy is required";
    }

    if (!formData.providerId) {
      newErrors.providerId = "Provider is required";
    }

    // Validate custom dosage instructions - now supporting multiple per medicine
    formData.medicines.forEach((med, index) => {
      if (med.useCustomDosage) {
        if (!med.customDosageInstructions || med.customDosageInstructions.length === 0) {
          newErrors[`medicines[${index}].customDosageInstructions`] = "At least one custom dosage instruction is required";
        } else {
          med.customDosageInstructions.forEach((instruction, instructionIndex) => {
            if (!instruction || instruction.trim() === "") {
              newErrors[`medicines[${index}].customDosageInstructions[${instructionIndex}]`] = "Custom dosage instruction cannot be empty";
            }
            if (instruction && instruction.trim().length > 500) {
              newErrors[`medicines[${index}].customDosageInstructions[${instructionIndex}]`] = "Instruction must be less than 500 characters";
            }
          });
        }
      }
    });

    let hasMedicineErrors = false;
    formData.medicines.forEach((med, index) => {
      // Check for medicine_id instead of id
      if (!med.medicine_id) {
        hasMedicineErrors = true;
        newErrors[`medicines[${index}].medicine_id`] = "Medicine is required";
      }

      // Check variant_id directly
      if (!med.variant_id) {
        hasMedicineErrors = true;
        newErrors[`medicines[${index}].variant_id`] = "Variant is required";
      }

      // Price validation
      if (
        !med.price ||
        isNaN(parseFloat(med.price)) ||
        parseFloat(med.price) < 0
      ) {
        hasMedicineErrors = true;
        newErrors[`medicines[${index}].price`] =
          "Price must be a valid number greater than or equal to 0";
      }

      // Check if dosage instructions are required but missing
      if (med.medicine_id && !med.useCustomDosage) {
        if (
          med.dosage_instructions &&
          med.dosage_instructions.length > 0 &&
          !med.dosage_instruction_id
        ) {
          hasMedicineErrors = true;
          newErrors[`medicines[${index}].dosage_instruction_id`] =
            "Dosage instruction is required";
        }
      }
    });

    if (hasMedicineErrors) {
      newErrors.medicines = "Please complete all medicine fields";
    }

    // if (!formData.selectedAddressId) {
    //   newErrors.selectedAddressId = "Shipping address is required";
    // }

    if (formData.selectedAddressId === "new") {
      if (!formData.newAddress.street) {
        newErrors["newAddress.street"] = "Address is required";
      }

      if (!formData.newAddress.city) {
        newErrors["newAddress.city"] = "City is required";
      }

      if (!formData.newAddress.state) {
        newErrors["newAddress.state"] = "State is required";
      }

      if (!formData.newAddress.zipcode) {
        newErrors["newAddress.zipcode"] = "Zipcode is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const [newAddress, setNewAddress] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (patientEditUnsaved) {
      setErrors((prev) => ({ ...prev, submit: "Please save patient info before creating the order." }));
      return;
    }
    if (!validateForm()) return;

    setLoading((prev) => ({ ...prev, submittingForm: true }));
    setErrors({});
    // Set once the order is saved: the button must stay disabled until the
    // redirect, or a second click creates a duplicate order
    let keepSubmitLocked = false;

    try {
      // Get user info from localStorage
      const userString = localStorage.getItem("user");
      if (!userString) {
        throw new Error("User information not found. Please log in again.");
      }
      const userObject = JSON.parse(userString);
      if (!userObject || !userObject.id) {
        throw new Error("User ID not found. Please log in again.");
      }
      console.log(
        "formDataformDataformDataformDataformDataformData",
        newAddress
      );
      const isNewAddress = formData.selectedAddressId === "new";
      const orderData = {
        patient_id: parseInt(formData.patientId),
        state_id: formData.stateId || null,
        pharmacy_id: formData.pharmacyId,
        provider_id: formData.providerId ? parseInt(formData.providerId) : null,
        // No longer need global custom_dosage array
        // custom_dosage: (formData.customDosageInstructions || [])
        //   .filter(instruction => instruction && instruction.trim() !== ""),
        order_items: formData.medicines.map((med) => {
          // Safely handle dosage instruction mapping
          let dosageInstructions = [];
          if (med.useCustomDosage && med.customDosageInstructions && med.customDosageInstructions.length > 0) {
            // Use custom dosage instructions - can be multiple
            dosageInstructions = med.customDosageInstructions
              .filter(instruction => instruction && instruction.trim() !== "")
              .map(instruction => ({
                dosage_instruction_id: null, // Custom instructions don't have DB IDs
                instruction: instruction.trim(),
                is_custom: true
              }));
          } else if (med.dosage_instruction_id) {
            // Use existing dosage instruction
            const foundInstruction = med.dosage_instructions?.find((di) => {
              const diId =
                di.dosage_instruction_id?.toString() ||
                di.dosage_id?.toString();
              return diId === med.dosage_instruction_id.toString();
            });

            if (foundInstruction) {
              dosageInstructions = [
                {
                  dosage_instruction_id: parseInt(med.dosage_instruction_id),
                  instruction:
                    foundInstruction.instruction ||
                    foundInstruction.dosage_instruction ||
                    "",
                  is_custom: false
                }
              ];
            } else {
              // Fallback if instruction not found but ID exists
              dosageInstructions = [
                {
                  dosage_instruction_id: parseInt(med.dosage_instruction_id),
                  instruction: "",
                  is_custom: false
                }
              ];
            }
          }

          return {
            medicine_id: parseInt(med.medicine_id),
            variant_id: parseInt(med.variant_id),
            price: parseFloat(med.price),
            compounding_context: (med.compounding_context || "").trim(),
            dosage_instructions: dosageInstructions
          };
        }),
        shipping_address: {
          ...newAddress,
          state_id: formData.stateId
        },
        new_address: {
          ...newAddress,
          state_id: formData.stateId
        },
        order_type: formData.orderType || "new",
        user_id: parseInt(userObject.id),
        shipping_fee: parseFloat(formData.shippingFee) || 0,
        discount: parseFloat(formData.discount) || 0,
        filled_by: formData.filledBy || userObject.username,
        notes: formData.notes || "",
        clinical_notes: formData.clinical_notes || "",

        shipping_address_id: formData.isNewAddress
          ? newAddress.address_id
          : id
          ? parseInt(formData.shipping_address_id)
          : formData.isNewAddress
          ? parseInt(formData.shipping_address_id)
          : parseInt(formData.patientAddress?.id),

        use_new_address: formData.isNewAddress,
        is_new_address: formData.isNewAddress
      };
      console.log("orderData is new", orderData);
      if (orderData.is_new_address) {
        // console.log("orderData is new", orderData);
        // orderData.shipping_address_id =
        //   orderData.shipping_address.address_id || "";
      }
      console.log("newAddress 1", newAddress);
      console.log("orderData 2", orderData);
      if (editMode && id) {
        await updateOrder(id, orderData);
        setSuccessMessage("Order updated successfully!");
      } else {
        // Use the new endpoint for creating orders
        const response = await api.post(
          "/api/orders/create-with-items/",
          orderData
        );
        if (response.data) {
          setApiResults(response.data.api_results);
          setShowStatusPopup(true);

          // If all APIs were successful, show success message and navigate
          if (response.data.api_results.overall_status) {
            keepSubmitLocked = true;
            setTimeout(() => {
              setShowStatusPopup(false);
              setSuccessMessage("Order created successfully!");
              setTimeout(() => {
                navigate("/orders");
              }, 600);
            }, 1200); // Long enough to read the per-API ticks
          }
        }
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      setErrors({
        submit:
          error.response?.data?.error ||
          error.message ||
          error.response?.data?.message ||
          "Failed to submit order"
      });
    } finally {
      if (!keepSubmitLocked) {
        setLoading((prev) => ({ ...prev, submittingForm: false }));
      }
    }
  };
  // Load pharmacies and medicines for a state
  const loadPharmaciesAndMedicines = async (stateId, isEditMode = false) => {
    setLoading((prev) => ({
      ...prev,
      loadingPharmacies: true,
      loadingMedicines: true
    }));

    try {
      const selectedState = states.find(
        (state) =>
          state.state_id && state.state_id.toString() === stateId.toString()
      );
      if (!selectedState || !selectedState.state_abbreviation) {
        console.error(
          "Selected state not found or missing abbreviation:",
          stateId
        );
        setErrors((prev) => ({
          ...prev,
          loadPharmacies: "Failed to load pharmacies: Invalid state selected."
        }));
        return;
      }

      const stateAbbreviation = selectedState.state_abbreviation;
      console.log(
        "Loading pharmacies and medicines for state:",
        stateAbbreviation
      );
      // setOriginalOrderData(orderData);
      const data = await fetchPharmaciesAndMedicines([stateAbbreviation]);
      console.log("Pharmacies and medicines response:", data);

      setPharmaciesAndMedicines(data);

      const pharmacyList = [];
      const medicineList = [];

      data.forEach((item) => {
        const pharmacy = {
          id: item.pharmacy_id,
          pharmacy_id: item.pharmacy_id,
          name: item.pharmacy_name,
          address: item.address,
          phone: item.phone,
          email: item.email
        };

        pharmacyList.push(pharmacy);

        item.medicines.forEach((medicine) => {
          medicineList.push({
            id: medicine.medicine_id,
            medicine_id: medicine.medicine_id,
            medicine_name: medicine.medicine_name,
            medicine_sku: medicine.medicine_sku,
            description: medicine.description,
            dosage: medicine.dosage,
            price: medicine.price,
            stock: medicine.stock,
            manufacturer: medicine.manufacturer,
            category: medicine.category,
            prescription_required: medicine.prescription_required,
            in_stock: medicine.stock > 0,
            state: medicine.state,
            pharmacy_id: pharmacy.pharmacy_id,
            pharmacy_name: pharmacy.pharmacy_name,
            dosage_instructions: medicine.dosage_instructions || [],
            variants: medicine.variants || []
          });
        });
      });

      console.log("Received pharmacies and medicines data:", {
        pharmacies: pharmacyList,
        medicines: medicineList
      });

      // If in edit mode and we have original order data, add the pharmacy from the order if not already in the list
      if (isEditMode && originalOrderData && originalOrderData.pharmacy) {
        const orderPharmacyId = originalOrderData.pharmacy.pharmacy_id;
        const pharmacyExists = pharmacyList.some(
          (p) => p.id == orderPharmacyId || p.pharmacy_id == orderPharmacyId
        );

        if (!pharmacyExists) {
          console.log(
            "Adding pharmacy from order to pharmacies list:",
            originalOrderData.pharmacy
          );
          pharmacyList.push({
            id: orderPharmacyId,
            pharmacy_id: orderPharmacyId,
            name: originalOrderData.pharmacy.pharmacy_name,
            address: originalOrderData.pharmacy.pharmacy_address,
            phone: "",
            email: ""
          });
        }
      }

      setPharmacies(pharmacyList);
      setMedicines(medicineList);
      reconcileSelectionsForState(stateId, pharmacyList, medicineList);

      // Only auto-select if not in edit mode (to avoid overriding the pharmacy from an existing order)
      if (pharmacyList.length === 1 && !formData.pharmacyId && !isEditMode) {
        setFormData((prev) => ({
          ...prev,
          pharmacyId: pharmacyList[0].id.toString()
        }));
      }
    } catch (err) {
      console.error("Error loading pharmacies and medicines:", err);
      setErrors((prev) => ({
        ...prev,
        loadPharmacies: "Failed to load pharmacies and medicines."
      }));
    } finally {
      setLoading((prev) => ({
        ...prev,
        loadingPharmacies: false,
        loadingMedicines: false
      }));
    }
  };

  // Calculate total order amount
  const calculateTotal = () => {
    const medicinesTotal = formData.medicines.reduce((sum, med) => {
      const price = parseFloat(med.price);
      const quantity = parseInt(med.quantity, 10) || 1;
      return sum + price * quantity;
    }, 0);

    const shippingFee = parseFloat(formData.shippingFee) || 0;
    const discount = parseFloat(formData.discount) || 0;

    return (medicinesTotal + shippingFee - discount).toFixed(2);
  };

  const resetForm = () => {
    setFormData({
      patientId: "",
      patientPhone: "",
      patientName: "",
      patientEmail: "",
      patientAddress: {
        street: "",
        city: "",
        state: "",
        zipcode: ""
      },
      selectedAddressId: "",
      newAddress: {
        street: "",
        address2: "",
        city: "",
        state: "",
        zipcode: ""
      },
      stateId: "",
      pharmacyId: "",
      medicines: [
        {
          id: "",
          name: "",
          quantity: 1,
          variant_id: "",
          price: 0,
          description: "",
          in_stock: true,
          medicine_sku: "",
          medicine_id: "",
          dosage_instructions: [],
          all_dosage_instructions: [],
          variants: [],
          useCustomDosage: false,
          customDosageInstructions: []
        }
      ],
      customDosageInstructions: [], // No longer needed
      shippingFee: 0,
      discount: 0,
      filledBy: "",
      notes: "",
      clinical_notes: "",
      addressSaved: false,
      orderType: "new",
      isNewAddress: false,
      pharmacy_addresses: [],
      selectedPharmacyAddressId: "",
      pharmacyAddress: {
        id: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: ""
      },
      providerId: ""
    });
    setErrors({});
    setSuccessMessage("");
    setPatientFound(false);
    setPatientAddresses([]);
  };

  return (
    <PageWrapper>
      <PageHeaderContainer>
        <PageTitle>{editMode ? "Edit Order" : "Create New Order"}</PageTitle>
        <PageDescription>
          {editMode
            ? "Update the details of an existing order"
            : "Fill out the form below to create a new medication order for a patient"}
        </PageDescription>
      </PageHeaderContainer>

      {successMessage && (
        <SuccessMessage>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          {successMessage}
        </SuccessMessage>
      )}

      <FormContainer>
        <form onSubmit={handleSubmit}>
          <PatientInfoSection
            editMode={editMode}
            originalOrderData={originalOrderData}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            loading={loading}
            patientFound={patientFound}
            setPatientFound={setPatientFound}
            patientAddresses={patientAddresses}
            setPatientAddresses={setPatientAddresses}
            states={states}
            loadPharmaciesAndMedicines={loadPharmaciesAndMedicines}
            handlePatientSearch={handlePatientSearch}
            onEditUnsavedChange={setPatientEditUnsaved}
          />

          <OrderRequestDetails
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            loading={loading}
            states={states}
            pharmacies={pharmacies}
            medicines={medicines}
            providersForState={providersForState}
            loadingProviders={loadingProviders}
            handleInputChange={handleInputChange}
            handlePharmacyChange={handlePharmacyChange}
            handleMedicationChange={handleMedicationChange}
            addMedicine={addMedicine}
            removeMedicine={removeMedicine}
            loadPharmaciesAndMedicines={loadPharmaciesAndMedicines}
            editMode={editMode}
            originalOrderData={originalOrderData}
          />

          <ShippingAddressSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            patientFound={patientFound}
            patientAddresses={patientAddresses}
            setPatientAddresses={setPatientAddresses}
            states={states}
            newAddress={newAddress}
            setNewAddress={setNewAddress}
            pharmacies={pharmacies}
            pharmacyAddressSync={pharmacyAddressSync}
            applyShippingAddressSelection={applyShippingAddressSelection}
            stateChangeNotices={stateChangeNotices}
            handleInputChange={handleInputChange}
            handleAddressChange={handleAddressChange}
            handlePharmacyAddressChange={(e) => {
              const selectedId = e.target.value;
              if (selectedId) {
                const selectedAddress = formData.pharmacy_addresses.find(
                  (address) => address.address_id.toString() === selectedId
                );

                if (selectedAddress) {
                  setFormData((prev) => ({
                    ...prev,
                    selectedPharmacyAddressId: selectedId,
                    pharmacyAddress: {
                      id: selectedAddress.address_id,
                      address1: selectedAddress.address1,
                      address2: selectedAddress.address2 || "",
                      city: selectedAddress.city,
                      state: selectedAddress.state,
                      zipcode: selectedAddress.zipcode
                    }
                  }));
                }
              } else {
                setFormData((prev) => ({
                  ...prev,
                  selectedPharmacyAddressId: "",
                  pharmacyAddress: {
                    id: "",
                    address1: "",
                    address2: "",
                    city: "",
                    state: "",
                    zipcode: ""
                  }
                }));
              }
            }}
          />

          <OrderSummary
            formData={formData}
            errors={errors}
            loading={loading}
            calculateTotal={calculateTotal}
            handleSubmit={handleSubmit}
            navigate={navigate}
            editMode={editMode}
            handleInputChange={handleInputChange}
          />
        </form>
      </FormContainer>

      {showStatusPopup && (
        <OrderStatusPopup
          apiResults={apiResults}
          onClose={() => setShowStatusPopup(false)}
        />
      )}
    </PageWrapper>
  );
}

export default CreateOrderPage;